import { create } from "zustand";
import { aggregateQuantityInfos } from '../utils/quantityUtils';
import { calculateUnitPrice } from '../components/orderhistory/aggregations';
import { toDate } from '../utils/dateUtils';
import { resolveProductIdentifier, isPricedProductLine } from '../utils/platformUtils';
import { getProductPrice } from '../components/orderhistory/vatUtils';

export interface CrossRestaurantPriceDiff {
  articleId: string;
  productName: string;
  platform: string;
  restaurantPrices: {
    restaurantId: string;
    restaurantName: string;
    price: number;
    unitPrice: number;
    unit: string;
    orderDate: string;
    orderHistoryId: string;
  }[];
  maxDiffPercent: number;
  daysBetweenOrders: number;
}

interface CalculationParams {
  orderHistory: any[];
  weekStart: Date;
  showExVat: boolean;
  selectedPlatforms: string[];
  minDiffPercent: number;
}

interface CrossRestaurantPricesState {
  // Cached calculations
  cachedPriceDifferences: CrossRestaurantPriceDiff[];
  cachedParams: CalculationParams | null;

  // Calculate price differences with memoization
  calculatePriceDifferences: (params: CalculationParams) => CrossRestaurantPriceDiff[];

  // Clear cache (useful for testing or forcing recalculation)
  clearCache: () => void;
}

const calculateCrossRestaurantPriceDiffs = (
  orderHistory: any[],
  weekStart: Date,
  showExVat: boolean
): CrossRestaurantPriceDiff[] => {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 7);

  const weekOrders = orderHistory.filter(order => {
    const orderDate = toDate(order.orderDate);
    return orderDate >= weekStart && orderDate < weekEnd;
  });

  const productMap = new Map<string, Array<{
    restaurantId: string;
    restaurantName: string;
    articleId: string;
    productName: string;
    platform: string;
    price: number;
    rawQuantity: number;
    quantityInfo: any;
    orderDate: Date;
    orderHistoryId: string;
  }>>();

  weekOrders.forEach(order => {
    if (!order.productData || !order.restaurantId) return;

    order.productData.forEach((product: any) => {
      // Identity, not the raw article id: the old key was `articleId-platform[-name]`, which merged
      // every product on a platform shipping the 'no_article_id' sentinel, and dropped products with
      // no article id at all (nespresso). Comparing prices across restaurants demands exact identity.
      const identifier = resolveProductIdentifier(order.platform, product.articleId, product.name);
      // Free (one-off gratis) and credit lines carry no comparable price: including them made the
      // latest-per-restaurant price 0, which the `minPrice === 0` guard below then turned into a
      // silently dropped comparison. Same rule as every other latest-price lookup.
      if (!identifier || !isPricedProductLine(product)) return;
      const key = `${identifier}-${order.platform}`;

      if (!productMap.has(key)) {
        productMap.set(key, []);
      }

      const price = getProductPrice(product, showExVat);

      productMap.get(key)!.push({
        restaurantId: order.restaurantId,
        restaurantName: order.restaurantName || 'Unknown',
        articleId: product.articleId,
        productName: product.name,
        platform: order.platform,
        price: price,
        rawQuantity: product.rawQuantity || 0,
        quantityInfo: product.quantityInfo,
        orderDate: toDate(order.orderDate),
        orderHistoryId: order.id
      });
    });
  });

  const diffs: CrossRestaurantPriceDiff[] = [];

  productMap.forEach((products, key) => {
    const restaurantMap = new Map<string, typeof products>();
    products.forEach(p => {
      if (!restaurantMap.has(p.restaurantId)) {
        restaurantMap.set(p.restaurantId, []);
      }
      restaurantMap.get(p.restaurantId)!.push(p);
    });

    if (restaurantMap.size < 2) return;

    const restaurantPrices: CrossRestaurantPriceDiff['restaurantPrices'] = [];

    restaurantMap.forEach((restaurantProducts, restaurantId) => {
      const aggregatedInfo = aggregateQuantityInfos(
        restaurantProducts.map(p => ({
          rawQuantity: p.rawQuantity,
          quantityInfo: p.quantityInfo,
          articleId: p.articleId,
          platform: p.platform,
          name: p.productName
        })),
        key
      );

      const latestProduct = restaurantProducts.sort((a, b) =>
        b.orderDate.getTime() - a.orderDate.getTime()
      )[0];

      let unitPrice = latestProduct.price;
      let unit = 'pc';

      if (latestProduct.quantityInfo && aggregatedInfo.hasQuantityData) {
        const unitPriceCalc = calculateUnitPrice(latestProduct.price, latestProduct.quantityInfo);
        if (unitPriceCalc) {
          unitPrice = unitPriceCalc.value;
          unit = unitPriceCalc.unit;
        }
      }

      restaurantPrices.push({
        restaurantId,
        restaurantName: latestProduct.restaurantName,
        price: latestProduct.price,
        unitPrice,
        unit,
        orderDate: latestProduct.orderDate.toISOString(),
        orderHistoryId: latestProduct.orderHistoryId
      });
    });

    const unitPrices = restaurantPrices.map(r => r.unitPrice);
    const minPrice = Math.min(...unitPrices);
    const maxPrice = Math.max(...unitPrices);

    if (minPrice === 0 || maxPrice === minPrice) return;

    const maxDiffPercent = ((maxPrice - minPrice) / minPrice) * 100;

    const orderDates = restaurantPrices.map(r => new Date(r.orderDate).getTime());
    const minDate = Math.min(...orderDates);
    const maxDate = Math.max(...orderDates);
    const daysBetweenOrders = Math.round((maxDate - minDate) / (1000 * 60 * 60 * 24));

    const firstProduct = products[0];
    diffs.push({
      articleId: firstProduct.articleId,
      productName: firstProduct.productName,
      platform: firstProduct.platform,
      restaurantPrices: restaurantPrices.sort((a, b) => a.unitPrice - b.unitPrice),
      maxDiffPercent,
      daysBetweenOrders
    });
  });

  return diffs.sort((a, b) => b.maxDiffPercent - a.maxDiffPercent);
};

const paramsEqual = (a: CalculationParams | null, b: CalculationParams): boolean => {
  if (!a) return false;

  return (
    a.orderHistory === b.orderHistory &&
    a.weekStart.getTime() === b.weekStart.getTime() &&
    a.showExVat === b.showExVat &&
    a.selectedPlatforms.length === b.selectedPlatforms.length &&
    a.selectedPlatforms.every((p, i) => p === b.selectedPlatforms[i]) &&
    a.minDiffPercent === b.minDiffPercent
  );
};

export const useCrossRestaurantPricesStore = create<CrossRestaurantPricesState>((set, get) => ({
  cachedPriceDifferences: [],
  cachedParams: null,

  calculatePriceDifferences: (params: CalculationParams) => {
    const state = get();

    // Check if we can use cached results
    if (paramsEqual(state.cachedParams, params)) {
      return state.cachedPriceDifferences;
    }

    // Calculate base price differences for the week
    const allPriceDifferences = calculateCrossRestaurantPriceDiffs(
      params.orderHistory,
      params.weekStart,
      params.showExVat
    );

    // Apply filters
    let filtered = allPriceDifferences;

    if (params.selectedPlatforms.length > 0) {
      filtered = filtered.filter(d =>
        params.selectedPlatforms.includes(d.platform.toLowerCase())
      );
    }

    filtered = filtered.filter(d => d.maxDiffPercent >= params.minDiffPercent);

    // Cache the results
    set({
      cachedPriceDifferences: filtered,
      cachedParams: params
    });

    return filtered;
  },

  clearCache: () => {
    set({
      cachedPriceDifferences: [],
      cachedParams: null
    });
  }
}));
